import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import ReactSelect from "react-select";
import { CSVLink } from "react-csv";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { paymentReportRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAllLecturesRequest } from "../../../modules/admin-modules/lectures/Actions";
import { getPaymentSubjectRequest } from "../../../modules/admin-modules/videos/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";
const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};
const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

const headers = [
  { label: "name", key: "name" },
  { label: "phone", key: "phone" },
  { label: "user_name", key: "user_name" },
  { label: "paid_amount", key: "paid_amount" },
  { label: "remaining_amount", key: "remaining_amount" },
  { label: "comment", key: "comment" },
  { label: "payment_subject_name", key: "payment_subject_name" },
  { label: "lecture_name", key: "lecture_name" },
  { label: "scan_by", key: "scan_by" },
  { label: "created_at", key: "created_at" },
];

const columns = [
  { Header: "#", accessor: "index" },
  { Header: "Student", accessor: "name" },
  { Header: "Phone", accessor: "phone" },
  { Header: "Code", accessor: "user_name" },
  { Header: "Paid", accessor: "paid_amount" },
  { Header: "Remaining", accessor: "remaining_amount" },
  { Header: "Comment", accessor: "comment" },
  { Header: "Payment subject", accessor: "payment_subject_name" },
  { Header: "Center", accessor: "lecture_name" },
  { Header: "Scanned by", accessor: "scan_by" },
  { Header: "Created at", accessor: "created_at" },
];

const PaymentReport = () => {
  const dispatch = useDispatch();
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [lectureId, setLectureId] = useState("");
  const [paymentSubjectId, setPaymentSubjectId] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(10);
  const setPage = () => {};

  useEffect(() => {
    dispatch(getAllLecturesRequest());
    dispatch(getPaymentSubjectRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!date || !lectureId) return;
    const payload = { date, lecture_id: lectureId };
    if (paymentSubjectId) payload.payment_subject_id = paymentSubjectId;
    dispatch(paymentReportRequest(payload));
  }, [date, lectureId, paymentSubjectId, dispatch]);

  const lectures = useSelector((state) => state.lectures.lectures);
  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const paymentReport = useSelector((state) => state.reports.paymentReport);
  const isLoading = useSelector((state) => state.reports.isLoading);

  const lectureOptions = useMemo(
    () =>
      (lectures || []).map((data) => ({
        value: data.id,
        label: data.title,
      })),
    [lectures]
  );

  const paymentSubjectOptions = useMemo(
    () =>
      (paymentSubjects || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [paymentSubjects]
  );

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = dayjs(isoString);
    return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : isoString;
  };

  const Data = useMemo(() => {
    const paymentList = Array.isArray(paymentReport) ? paymentReport : [];
    return paymentList.map((data, index) => ({
      ...data,
      index: `#${index + 1}`,
      id: data?.id,
      name: data?.name,
      phone: data?.phone,
      user_name: data?.user_name,
      paid_amount: data?.paid_amount,
      remaining_amount: data?.remaining_amount,
      comment: data?.comment,
      payment_subject_name:
        data?.payment_subject?.name ?? data?.payment_subject_name ?? "",
      lecture_name:
        data?.lecture_id?.name ?? data?.lecture_id?.title ?? data?.lecture_name ?? "",
      scan_by: data?.scan_by ?? "",
      created_at: formatDate(data?.created_at),
    }));
  }, [paymentReport]);

  const canLoad = Boolean(date && lectureId);

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Paid report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students who paid for the selected center session.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align={{ base: "stretch", md: "flex-end" }}>
          <Box minW={{ base: "100%", md: "200px" }} maxW={{ md: "240px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Date
            </Text>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              borderRadius="14px"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              fontWeight="600"
              _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
            />
          </Box>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Center
            </Text>
            <ReactSelect
              options={lectureOptions}
              isMulti={false}
              onChange={(opt) => setLectureId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select center"
              value={lectureOptions.find((o) => o.value === lectureId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Payment subject
            </Text>
            <ReactSelect
              options={paymentSubjectOptions}
              isMulti={false}
              onChange={(opt) => setPaymentSubjectId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Optional"
              value={paymentSubjectOptions.find((o) => o.value === paymentSubjectId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          {Data.length > 0 ? (
            <CSVLink
              headers={headers}
              data={Data}
              filename={`paid-report-${date}.csv`}
              target="_blank"
            >
              <Button
                size="sm"
                h="44px"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        <Box {...cardSx} p={{ base: 4, md: 6 }}>
          {!canLoad ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a date and center to load paid students.
            </Text>
          ) : (
            <CTable
              selectedData={() => {}}
              Columns={columns}
              Data={Data}
              Actions
              currentpage={pageNumber}
              setPage={setPage}
              setPageNumber={setPageNumber}
              setPerPage={setPerPage}
              totalPage={Math.ceil((Data.length || 0) / size) || 1}
              isLoading={isLoading}
              perPage={size}
              noSearchBar
              noFilter
              noSecondSearchBar
              wrapperProps={{
                borderWidth: "1px",
                borderColor: "var(--border-subtle)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default PaymentReport;
